/**
 * Fable 검증 상태 페이지 공용 문구 도우미.
 * 쉬운 한국어를 1차 문구로, 내부 용어는 보조 라벨로 병기한다.
 */
import type { DailyTacticalReadiness } from '../../services/operationsApi';

export type FableTone = 'safe' | 'info' | 'warn' | 'danger';

export function cohortLabel(cohortId: string): string {
  if (cohortId.includes('REHEARSAL')) return '사전 리허설';
  if (cohortId.includes('PLUMBING')) return '배관 테스트';
  return '공식 관찰';
}

export function modeLabel(mode: string): string {
  if (mode === 'rehearsal') return '사전 리허설';
  if (mode === 'official') return '공식 관찰';
  if (mode === 'plumbing') return '배관 테스트';
  return mode;
}

export interface BlockerInfo {
  title: string;
  detail: string;
  tone: FableTone;
}

const BLOCKER_MAP: Record<string, BlockerInfo> = {
  real_model_disabled: {
    title: '실제 AI 모델 연결이 꺼져 있음',
    detail: '공식 관찰은 실제 모델 응답으로만 기록해야 하므로, 시작 전에 켜야 합니다.',
    tone: 'warn',
  },
  scheduler_disabled: {
    title: '자동 실행이 꺼져 있음',
    detail: '지금(리허설 단계)은 꺼짐이 정상이며 의도된 안전 상태입니다. 공식 시작 승인 시에만 켭니다.',
    tone: 'info',
  },
  official_cohort_absent: {
    title: '공식 관찰 기록이 아직 0건',
    detail: '공식 관찰 시작(official Stage A) 승인 전이므로 0건 유지가 정상입니다.',
    tone: 'info',
  },
  live_broker_orders_present: {
    title: '실제 주문 기록이 발견됨',
    detail: '있어서는 안 되는 상태입니다. 즉시 사용자와 Codex에 보고해야 합니다.',
    tone: 'danger',
  },
  official_evidence_incomplete: {
    title: '공식 기록의 근거 묶음이 불완전함',
    detail: '가장 최근 공식 기록에 필수 근거(시장/유니버스/프롬프트)가 빠져 있습니다.',
    tone: 'danger',
  },
  market_payload_coverage_incomplete: {
    title: '시장 가격 근거가 아직 부족함',
    detail: '저장된 MARKET 데이터가 가상 진입·청산에 필요한 open/high/low/next open을 제공하지 못합니다.',
    tone: 'warn',
  },
  weekly_model_rerun_audit_not_implemented: {
    title: '모델 재현 감사 기능 미구현',
    detail: '같은 근거로 모델을 다시 호출해 판단이 흔들리는지 확인하는 기능이 필요합니다.',
    tone: 'warn',
  },
  weekly_fact_check_audit_not_implemented: {
    title: '근거 사실 점검 기능 미구현',
    detail: '뉴스·시세·프리마켓 근거가 저장·검증 가능한지 확인하는 기능이 필요합니다.',
    tone: 'warn',
  },
};

export function blockerInfo(code: string): BlockerInfo {
  return BLOCKER_MAP[code] || {
    title: '알 수 없는 시작 전 조건',
    detail: '새로 추가된 blocker 코드입니다. 문서를 확인해야 합니다.',
    tone: 'warn',
  };
}

const OPERATIONAL_ISSUE_LABELS: Record<string, string> = {
  model_output_not_valid: 'AI 판단 결과 형식이 올바르지 않음',
  model_output_invalid: 'AI 판단 결과 형식이 올바르지 않음',
  market_payload_coverage_incomplete: '가상 매매 계산에 필요한 시세가 부족함',
  scheduler_disabled: '자동 실행이 꺼져 있음',
  official_cohort_absent: '공식 관찰 기록이 아직 없음',
};

export function operationalIssueLabel(code: string): string {
  return OPERATIONAL_ISSUE_LABELS[code] || `확인이 필요한 항목 (${code})`;
}

export function auditCohortId(readiness: DailyTacticalReadiness): string {
  const mode = readiness.flags.cohort_mode;
  if (mode === 'official') return readiness.official_cohort_id;
  if (mode === 'plumbing') return readiness.plumbing_cohort_id;
  return readiness.rehearsal_cohort_id;
}

export function formatKst(iso: string | null | undefined): string {
  if (!iso) return '데이터 없음';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '데이터 없음';
  return date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
